import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { formatDate, formatMonth } from "./utils";
import { IPosicaoCadastralApi } from "./types";
import { useAlert } from "contexts/alert";
import { cpfMask } from "utils/cpf";
import api from "services/api";

import { Box, Button, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Modal, Select, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from '@mui/x-data-grid/locales';
import { Close } from "@mui/icons-material";
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EyeSVG from "components/svg/eye";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import Dropzone from "react-dropzone";
import { UploadContainer } from "./styled";
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

type Filters = {
  personName: string;
  personType: 'all' | 'employee' | 'dependent';
  plan: string;
}

type Props = {
  posicaoWorkspaceActivityId: string
}

export function View({ posicaoWorkspaceActivityId }: Props) {

  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string; };
  const { openAlert } = useAlert();

  const [isModalStatusOpen, setIsModalStatusOpen] = useState(false);
  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [inputMonth, setInputMonth] = useState<Dayjs | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const [currentFilters, setCurrentFilters] = useState<Filters>({
    personName: '',
    personType: 'all',
    plan: 'all',
  });

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["posicao-cadastral", workspaceId],
    queryFn: () => api.get<{ data: IPosicaoCadastralApi }>(`/v2/posicao-cadastral/${posicaoWorkspaceActivityId}`),
    select: (data) => data.data.data,
    refetchInterval: 5000,
  });

  function handleFilters(filters: Filters) {
    setCurrentFilters({ ...filters });
  };

  // Definição das colunas do DataGrid
  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'taxPayerId', 
      headerName: 'CPF Beneficiário', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? cpfMask(params.value as string) : '-'
    },
    { 
      field: 'taxPayerIdEmployee', 
      headerName: 'CPF Titular', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? cpfMask(params.value as string) : '-'
    },
    { 
      field: 'beneficiaryType', 
      headerName: 'Tipo do beneficiário', 
      width: 160,
      renderCell: (params: GridRenderCellParams) => 
        params.value === 'employee' ? 'Titular' : 'Dependente'
    },
    { 
      field: 'plan', 
      headerName: 'Plano', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'planStartDate', 
      headerName: 'Inicio da vigência', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? dayjs(params.value).format("DD/MM/YYYY") : '-'
    },
    { 
      field: 'premio', 
      headerName: 'Premio', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'situation', 
      headerName: 'Situação de limear', 
      width: 160,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    }
  ];

  // Filtragem e transformação dos dados
  const filteredData = data ? data.people
    .filter(person => 
      (person?.name?.toLowerCase()?.includes(currentFilters.personName.toLowerCase()) || 
       person?.taxPayerId?.toLowerCase()?.includes(currentFilters.personName.toLowerCase()) ||
       person?.taxPayerIdEmployee?.toLowerCase()?.includes(currentFilters.personName.toLowerCase())) &&
      (currentFilters.personType === 'all' || currentFilters.personType === person.beneficiaryType) &&
      (currentFilters.plan === 'all' || currentFilters.plan === person.plan)
    )
    .sort((a, b) => 
      a.taxPayerIdEmployee === null ? -1 : 
      b.taxPayerIdEmployee === null ? -1 : 
      a.taxPayerIdEmployee.localeCompare(b.taxPayerIdEmployee)
    ) : [];

  // Transformação dos dados para o DataGrid
  const rows = filteredData.map((person, index) => ({
    id: `${person.taxPayerId}_${index}`,
    name: person.name,
    taxPayerId: person.taxPayerId,
    taxPayerIdEmployee: person.taxPayerIdEmployee,
    beneficiaryType: person.beneficiaryType,
    plan: person.plan,
    planStartDate: person.planStartDate,
    premio: person.premio,
    situation: person.situation
  }));

  async function handleUpload() {
    try {
      if (!inputMonth || !file) throw new Error("Input Month and file are mandatory.");

      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file as Blob);
      formData.append("workspaceActivityId", posicaoWorkspaceActivityId);
      formData.append("monthReference", dayjs(inputMonth).format("MM/YYYY"));
  
      await api.post("/conducco-cdp-apps/bate-cadastral/posicao-cadastral", formData, { headers: { "Content-Type": "multipart/form-data" }});

      setFile(null);
      setInputMonth(null);
      openAlert('Arquivo enviado com sucesso', 'success');
    } catch (error) {
      openAlert('Houve algum erro ao enviar o arquivo.', 'error');
    } finally {
      setIsUploading(false);
      setIsModalUploadOpen(false);
    }
  }

  return (
    <>
      {!isFetched && isFetching && <div>Loading...</div>}
      {!isFetched && isError && <div>Error...</div>}
      {data && (
        <>
          <Modal open={isModalStatusOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 800, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Atividades relacionadas</h6>
                <Close style={{"cursor":"pointer"}} onClick={() => setIsModalStatusOpen(false)}/>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
                <EyeSVG />
                <span style={{ fontSize: '22px', fontWeight: '600', color: '#0F0F1E99' }}>Não há atividades relacionadas</span>
                <span style={{ fontSize: '16px', width: '550px', textAlign: 'center', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Esta atividade não tem atividades relacionada para atualização de dados.</span>
              </div>
            </Box>
          </Modal>

          <Modal open={isModalUploadOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Carregar nova posição cadastral</h6>
                <Close style={{"cursor":"pointer"}} 
                  onClick={() => {
                    setFile(null)
                    setInputMonth(null)
                    setIsModalUploadOpen(false)
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
                <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Ao carregar uma nova posição cadastral, a última posição cadastral enviada será <strong>arquivada</strong> e você poderá consultar histórico.</span>
                <DatePicker
                  label="Mês referente"
                  format="MM/YYYY"
                  openTo="month"
                  views={['year', 'month']}
                  value={inputMonth}
                  onChange={(e) => !!e ? setInputMonth(e) : setInputMonth(null)}
                />
                {!!file && (
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <InsertDriveFileOutlinedIcon style={{ width: "24px" }}/>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#191C1A' }}>{file.name}</span>
                    <DeleteIcon color="error" style={{ width: "24px", cursor: "pointer" }} onClick={() => setFile(null)}/>
                  </div>
                )}
                {!file && (
                  <Dropzone
                    onDrop={(acceptedFiles) => !!acceptedFiles.length ? setFile(acceptedFiles[0]) : setFile(null)}
                    accept={{
                      'application/vnd.ms-excel': ['.xls'],
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                    }}
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <div className="container" style={{ marginTop: "-24px" }}>
                        <UploadContainer {...getRootProps({ className: 'dropzone' })}>
                          <input {...getInputProps()} />
                          <FileUploadIcon style={{ fontSize: '24px', color: '#3024E1' }} />
                          {file ? (
                            <p style={{ color: "#3024E1", fontSize: '16px', margin: '0', fontWeight: 500 }}>
                              Arquivo selecionado
                            </p>
                          ) : isDragActive ? (
                            <>
                              <p style={{ color: "#3024E1", fontSize: '16px', margin: '0' }}>Solte o arquivo aqui…</p>
                              <p style={{ fontSize: "12px", margin: '0', color: '#0F0F1E99' }}>Liberando para upload</p>
                            </>
                          ) : (
                            <>
                              <p style={{ color: "#3024E1", fontSize: '16px', margin: '0' }}>Arraste e solte ou clique para procurar</p>
                              <p style={{ fontSize: "12px", margin: '0', color: '#0F0F1E99' }}>Somente arquivos nos formatos xls e xlsx são aceitos</p>
                            </>
                          )}
                        </UploadContainer>
                      </div>
                    )}
                  </Dropzone>
                )}
              </div>
               <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
                  <div style={{"display":"flex","gap":"0.25rem"}}>
                    <Button
                      variant='outlined'
                      color='error'
                      style={{minWidth: "180px","paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                      onClick={() => {
                        setFile(null)
                        setInputMonth(null)
                        setIsModalUploadOpen(false)
                      }}
                      disabled={isUploading}
                    >
                      Cancelar
                    </Button>
        
                    <Button
                      variant='contained'
                      style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                      onClick={handleUpload}
                      disabled={isUploading || !inputMonth || !file}
                    >
                      {!isUploading ? "Carregar" : <CircularProgress size="20px"/>}
                    </Button>
                  </div>
                </div>
            </Box>
          </Modal>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600,marginBottom: '24px'}}>Carregar posição cadastral</h1>
  
            <div style={{"display":"flex","gap":"0.25rem", alignItems: "center"}}>
              <Button variant="outlined" style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "8px", height: "30px", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                onClick={() => setIsModalStatusOpen(true)}
              >
                Atividades relacionadas
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', gap: '50px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Posição atualizada em</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatDate(data.dates.executedAt)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Referente ao mês</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatMonth(data.dates.monthReference)}</span>
            </div>
          </div>

          <div style={{"display":"flex","gap":"1rem"}}>
            {Object.entries(data.metrics).map(([title, value]) => (
              <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{title === 'countActive' ? "Total de registros na posicão ativas" : title === 'countEmployees' ? "Titulares" : title === 'countDependents' ? "Dependentes" : "Valor total (Premio)"}</p>
                <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{title === 'sumPremio' ? `R$${value},00` : value}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                  },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              style={{ backgroundColor: "white" }}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                "& .MuiDataGrid-root": {
                  border: 'none',
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: '#ffffff',
                  "&::-webkit-scrollbar": {
                    width: "12px",
                    height: "12px"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#C1C1C1",
                    borderRadius: "8px"
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#F5F5F5"
                  }
                },
              }}
            />
          </div>

          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end',"paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalUploadOpen(true)}
            >
              Carregar nova posição cadastral
            </Button>
          </div>
        </>
      )}
    </>
  );
}
